import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import OrganizationSetting from "@/services/organization/organization-setting";
import { GetAutoBucketSettings } from "@/dtos/OrganizationSetting.dto";

export interface AutoBucketSettingsParams {
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useAutoBucketSettings(
  params: Ref<AutoBucketSettingsParams | null> | AutoBucketSettingsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["autoBucketSettings", "disabled"];
    return ["autoBucketSettings", p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await OrganizationSetting.getAutoBucketSettings({
        organizationId: p.organizationId,
      } as GetAutoBucketSettings);

      let data = null;
      if (result.data?.status && result.data?.data) {
        data = result.data.data;
      } else if (result.data && !result.data.status) {
        data = result.data;
      }

      if (data) {
        return {
          autoBucketEnabled: data.autoBucketEnabled ?? true,
          autoBucketLinkedInOption: data.autoBucketLinkedInOption || null,
          autoBucketEmailOption: data.autoBucketEmailOption || null,
          autoBucketWhatsAppOption: data.autoBucketWhatsAppOption || null,
          autoBucketInMailOption: data.autoBucketInMailOption || null,
          autoBucketSMSOption: data.autoBucketSMSOption || null,
          autoBucketFollowUpDateOffset: data.autoFollowUpDate ?? 0,
          autoBucketExcludedOptionIds: data.autoBucketExcludedOptionIds || [],
          signupContactBucketOption: data.signupContactBucketOption || null,
        };
      }

      return {
        autoBucketEnabled: true,
        autoBucketLinkedInOption: null,
        autoBucketEmailOption: null,
        autoBucketWhatsAppOption: null,
        autoBucketInMailOption: null,
        autoBucketSMSOption: null,
        autoBucketFollowUpDateOffset: 0,
        autoBucketExcludedOptionIds: [],
        signupContactBucketOption: null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
